import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Member } from '@nexbit/types'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { LogOut, User, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT, useLang } from '@/contexts/language'
import { useAuth } from '@/contexts/auth'

interface HeaderProps {
  onMenuToggle?: () => void
  className?: string
}

function getInitials(member: Member | null): string {
  if (!member) return '?'
  const name = member.displayName ?? member.username
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
}

export function Header({ onMenuToggle, className }: HeaderProps) {
  const navigate = useNavigate()
  const t = useT()
  const { toggle } = useLang()
  const { user, logout, isAdmin, isMaster } = useAuth()
  const appLabel = isAdmin ? 'Lotmatic Admin' : isMaster ? 'Lotmatic Master' : 'Lotmatic Agent'
  // One build serves the admin, master and agent consoles, so index.html can only
  // carry one title; the tab takes the signed-in console's own name instead.
  useEffect(() => { document.title = appLabel }, [appLabel])

  function handleLogout() {
    logout()
    navigate('/login')
  }

  return (
    <header
      className={cn(
        'flex items-center justify-between px-4 h-12 border-b border-border bg-card shrink-0',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        {onMenuToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            className="md:hidden h-8 w-8"
            aria-label="เปิด/ปิดเมนู"
          >
            <Menu className="h-4 w-4" />
          </Button>
        )}
        <span className="text-xs font-medium text-muted-foreground hidden sm:block tracking-wide">
          {appLabel}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggle}
          className="h-7 px-2 font-mono text-xs text-muted-foreground hover:text-foreground"
          aria-label={t.lang.toggleLabel}
        >
          {t.lang.toggle}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="p-0 h-8 w-8 rounded-full"
              aria-label={`เมนูผู้ใช้: ${user?.username ?? 'ผู้ใช้'}`}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
                  {getInitials(user)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold truncate">{user?.username ?? '...'}</span>
                {user?.displayName && (
                  <span className="text-xs text-muted-foreground truncate">{user.displayName}</span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => navigate('/history')}>
              <User className="h-3.5 w-3.5" />
              <span className="text-sm">{t.header.history}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 cursor-pointer text-destructive focus:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="text-sm">{t.header.logout}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
